import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { EventService } from '../core/services/event.service';
import { EncryptionService } from '../core/services/encryption.service';
import { ToastService } from '../core/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { CoverImageComponent } from '../cover-image/cover-image.component';
import { InviteDialogComponent } from '../invite-dialog/invite-dialog.component';
import { openInviteShareDialog } from '../invite-share/open-invite-share';
import { environment } from '../../environments/environment';
import { toDate, unixToDate, durationParts, isSameOrBefore } from '../utils/date.utils';
import { reloadApp } from '../core/utils/reload-app';

@Component({
    selector: 'app-event-settings-v2',
    templateUrl: './event-settings-v2.component.html',
    styleUrls: ['./event-settings-v2.component.css']
})
export class EventSettingsV2Component implements OnInit {
    newEventForm: FormGroup;
    isMobile = false;
    originalFormValues: any = null;
    eventData: any = null;
    inviteInfo: any = null;
    eventId: string | null = null;
    isSaving = false;
    isEditable = false;
    isCreating = false;
    useNow = false;
    eventDuration: string = '';
    coverImage: string | null = null;
    subfolders: any[] = [];
    showFolderDialog = false;
    folderAction = 'add';
    folderName = '';
    editingFolderIndex = -1;
    categories: any[] = [];
    selectedCategory: any = null;
    collaborators: any[] = [];
    allCollaborators: any[] = [];
    selectedFilter = 'all';
    currentSection = 1;
    activeMenuIndex: number | null = null;
    qrImage: string | null = null;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private eventService: EventService,
        private encryptionService: EncryptionService,
        private toastService: ToastService,
        private dialog: MatDialog
    ) {
        this.newEventForm = this.fb.group({
            eventName: ['', Validators.required],
            eventHost: ['', Validators.required],
            eventCategory: [''],
            eventStartTime: ['', Validators.required],
            eventEndTime: ['', Validators.required],
            inviteMessage: [''],
            RSVP: [false],
            eventLocation: [''],
            isScheduled: [false]
        }, {
            validators: [this.validateEndTimeAfterStartTimeAndMaxDuration.bind(this)]
        });
    }

    async ngOnInit(): Promise<void> {
        this.updateIsMobile();
        this.loadFromLocalStorage();
        this.getCategories();

        // Load event subfolders
        const subfoldersStr = localStorage.getItem('event_subfolders');
        if (subfoldersStr) {
            try {
                this.subfolders = JSON.parse(subfoldersStr);
            } catch (e) {
                // console.warn('Failed to parse subfolders', e); //__CONSOLE_LOG__
            }
        }

        // Load collaborators from invite history
        if (this.eventId) {
            try {
                (await this.eventService.getInviteHistory(this.eventId)).subscribe({
                    next: (res: any) => {
                        if (res && res.result === 'success' && res.data) {
                            this.allCollaborators = res.data.map((user: any) => ({
                                ...user,
                                digitalRSVP: user.RSVP || 'pending'
                            }));
                            this.collaborators = [...this.allCollaborators];
                        }
                    },
                    error: (err: any) => console.error('Error loading collaborators:', err)
                });
            } catch (e) {
                // console.error('Error fetching invite history:', e); //__CONSOLE_LOG__
            }
        }

        // Load QR image
        this.qrImage = localStorage.getItem('qrImage') || localStorage.getItem('web_qrImage');

        // Setup RSVP validation
        this.newEventForm.get('RSVP')?.valueChanges.subscribe(rsvp => {
            const locationControl = this.newEventForm.get('eventLocation');
            if (rsvp) {
                locationControl?.setValidators([Validators.required]);
            } else {
                locationControl?.clearValidators();
            }
            locationControl?.updateValueAndValidity();
        });
    }

    updateIsMobile(): void {
        this.isMobile = window.innerWidth <= 768;
    }

    loadFromLocalStorage(): void {
        this.eventId = localStorage.getItem('eventId');
        this.coverImage = localStorage.getItem('coverImage');

        const inviteInfoStr = localStorage.getItem('invite-info');
        if (inviteInfoStr) {
            try {
                const decrypted = this.encryptionService.decryptData(inviteInfoStr);
                this.inviteInfo = JSON.parse(decrypted);
            } catch (e) {
                try {
                    this.inviteInfo = JSON.parse(inviteInfoStr);
                } catch (e2) {
                    // console.warn('Failed to parse invite-info', e2); //__CONSOLE_LOG__
                }
            }
        }

        const eventDataStr = localStorage.getItem('eventData');
        if (eventDataStr) {
            try {
                const decrypted = this.encryptionService.decryptData(eventDataStr);
                this.eventData = JSON.parse(decrypted);
            } catch (e) {
                try {
                    this.eventData = JSON.parse(eventDataStr);
                } catch (e2) {
                    // console.warn('Failed to parse eventData', e2); //__CONSOLE_LOG__
                }
            }

            if (this.eventData) {
                const startTime = this.eventData.Start_time ? unixToDate(this.eventData.Start_time) : null;
                const endTime = this.eventData.End_time ? unixToDate(this.eventData.End_time) : null;

                this.newEventForm.patchValue({
                    eventName: this.eventData.name || '',
                    eventHost: this.eventData.host || '',
                    eventCategory: this.eventData.category || '',
                    eventStartTime: startTime ? startTime : '',
                    eventEndTime: endTime ? endTime : '',
                    inviteMessage: this.eventData.invite_note || '',
                    RSVP: this.eventData.RSVP || false,
                    eventLocation: this.eventData.location || '',
                    isScheduled: this.eventData.isScheduled || false
                });

                if (this.eventData.category) {
                    this.selectedCategory = { name: this.eventData.category };
                }

                this.calculateDuration();
                this.originalFormValues = this.newEventForm.value;
                this.newEventForm.disable();
            }
        }
    }

    toggleEdit(): void {
        this.isEditable = !this.isEditable;
        if (this.isEditable) {
            this.newEventForm.enable();
        } else {
            this.cancel();
        }
    }

    cancel(): void {
        if (this.originalFormValues) {
            this.newEventForm.patchValue(this.originalFormValues);
        }
        this.newEventForm.disable();
        this.isEditable = false;
    }

    toggleNowCheckbox(): void {
        this.useNow = !this.useNow;
        if (this.useNow) {
            this.newEventForm.patchValue({
                eventStartTime: new Date()
            });
        }
    }

    onStartTimeChange(): void {
        this.calculateDuration();
    }

    onEndTimeChange(): void {
        this.calculateDuration();
    }

    calculateDuration(): void {
        const startTime = this.newEventForm.get('eventStartTime')?.value;
        const endTime = this.newEventForm.get('eventEndTime')?.value;

        if (startTime && endTime) {
            const { days, hours, minutes } = durationParts(startTime, endTime);

            let durationStr = '';
            if (days > 0) durationStr += `${days}d `;
            if (hours > 0) durationStr += `${hours}h `;
            if (minutes > 0) durationStr += `${minutes}m`;

            this.eventDuration = durationStr.trim() || '0m';
        } else {
            this.eventDuration = '';
        }
    }

    validateEndTimeAfterStartTimeAndMaxDuration(control: AbstractControl): ValidationErrors | null {
        const startTime = control.get('eventStartTime')?.value;
        const endTime = control.get('eventEndTime')?.value;

        if (!startTime || !endTime) {
            return null;
        }

        if (isSameOrBefore(endTime, startTime)) {
            return { endBeforeStart: true };
        }

        const { totalDays } = durationParts(startTime, endTime);
        if (totalDays > 10) {
            return { durationExceeds: true };
        }

        return null;
    }

    async getCategories(): Promise<void> {
        try {
            (await this.eventService.getAllCategories()).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success' && res.data) {
                        this.categories = res.data;
                    }
                },
                error: (err: any) => console.error('Error fetching categories:', err)
            });
        } catch (e) {
            // console.error('Error calling getAllCategories:', e); //__CONSOLE_LOG__
        }
    }

    onFilterOptionSelected(category: any): void {
        this.selectedCategory = category;
        this.newEventForm.patchValue({ eventCategory: category.name });
    }

    changeCover(): void {
        this.openCoverPopup();
    }

    openCoverPopup(): void {
        const dialogRef = this.dialog.open(CoverImageComponent, {
            width: this.isMobile ? '95vw' : '500px',
            maxWidth: '95vw',
            panelClass: 'cover-image-dialog'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.coverImage) {
                this.coverImage = result.coverImage;
                if (this.coverImage) {
                    localStorage.setItem('coverImage', this.coverImage);
                }
            }
        });
    }

    async generateMessage(): Promise<void> {
        const eventName = this.newEventForm.get('eventName')?.value;
        const eventCategory = this.newEventForm.get('eventCategory')?.value;

        if (!eventName) {
            this.toastService.showToast('Please enter event name first');
            return;
        }

        try {
            (await this.eventService.generateAIresponse(eventName, eventCategory || '')).subscribe({
                next: (res: any) => {
                    if (res && res.data && res.data.message) {
                        this.newEventForm.patchValue({ inviteMessage: res.data.message });
                        this.toastService.showToast('AI message generated!');
                    }
                },
                error: (err: any) => {
                    // console.error('AI generation error:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to generate message');
                }
            });
        } catch (e) {
            // console.error('Error calling AI service:', e); //__CONSOLE_LOG__
        }
    }

    // Folder/Album Management
    openFolderDialog(action: string, index: number = -1): void {
        this.folderAction = action;
        this.editingFolderIndex = index;
        if (action === 'edit' && index >= 0 && this.subfolders[index]) {
            this.folderName = this.subfolders[index].name;
        } else {
            this.folderName = '';
        }
        this.showFolderDialog = true;
    }

    closeFolderDialog(): void {
        this.showFolderDialog = false;
        this.folderName = '';
        this.editingFolderIndex = -1;
    }

    async addSubfolder(): Promise<void> {
        if (!this.folderName.trim()) {
            this.toastService.showToast('Please enter folder name');
            return;
        }

        if (!this.eventId) {
            this.toastService.showToast('We lost track of which album this is. Go back and open it again.');
            return;
        }

        try {
            (await this.eventService.saveSubFolder(this.eventId, this.folderName, null)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success' && res.data) {
                        this.subfolders.push({ name: this.folderName, id: res.data.subfolder_id });
                        localStorage.setItem('event_subfolders', JSON.stringify(this.subfolders));
                        this.toastService.showToast('Folder added successfully!');
                        this.closeFolderDialog();
                    }
                },
                error: (err: any) => {
                    // console.error('Error adding folder:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to add folder');
                }
            });
        } catch (e) {
            // console.error('Error calling saveSubFolder:', e); //__CONSOLE_LOG__
        }
    }

    async editSubfolder(): Promise<void> {
        if (!this.folderName.trim()) {
            this.toastService.showToast('Please enter folder name');
            return;
        }

        if (this.editingFolderIndex < 0 || !this.subfolders[this.editingFolderIndex]) {
            return;
        }

        const folder = this.subfolders[this.editingFolderIndex];
        if (!folder.id) {
            return;
        }

        try {
            (await this.eventService.saveSubFolder(this.eventId!, this.folderName, folder.id)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success') {
                        this.subfolders[this.editingFolderIndex].name = this.folderName;
                        localStorage.setItem('event_subfolders', JSON.stringify(this.subfolders));
                        this.toastService.showToast('Folder updated successfully!');
                        this.closeFolderDialog();
                    }
                },
                error: (err: any) => {
                    // console.error('Error editing folder:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to update folder');
                }
            });
        } catch (e) {
            // console.error('Error calling saveSubFolder:', e); //__CONSOLE_LOG__
        }
    }

    async removeSubfolder(index: number): Promise<void> {
        if (index < 0 || !this.subfolders[index]) {
            return;
        }

        const folder = this.subfolders[index];
        if (!folder.id) {
            return;
        }

        const confirmed = confirm(`Are you sure you want to delete "${folder.name}"?`);
        if (!confirmed) {
            return;
        }

        try {
            (await this.eventService.removeSubFolder(this.eventId!, folder.id)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success') {
                        this.subfolders.splice(index, 1);
                        localStorage.setItem('event_subfolders', JSON.stringify(this.subfolders));
                        this.toastService.showToast('Folder removed successfully!');
                    }
                },
                error: (err: any) => {
                    // console.error('Error removing folder:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to remove folder');
                }
            });
        } catch (e) {
            // console.error('Error calling removeSubFolder:', e); //__CONSOLE_LOG__
        }
    }

    async previewInvite(): Promise<void> {
        if (this.newEventForm.invalid) {
            this.newEventForm.markAllAsTouched();
            this.toastService.showToast('Please fill all required fields');
            return;
        }

        const formErrors = this.newEventForm.errors;
        if (formErrors) {
            if (formErrors['endBeforeStart']) {
                this.toastService.showToast('End time must be after start time');
                return;
            }
            if (formErrors['durationExceeds']) {
                this.toastService.showToast('Event duration cannot exceed 10 days');
                return;
            }
        }

        this.isSaving = true;
        const val = this.newEventForm.value;

        const startEpoch = val.eventStartTime ? Math.floor(toDate(val.eventStartTime).getTime() / 1000) : null;
        const endEpoch = val.eventEndTime ? Math.floor(toDate(val.eventEndTime).getTime() / 1000) : null;

        try {
            (await this.eventService.updateScheduledEvent(
                this.eventId,
                val.eventName,
                val.eventHost,
                val.inviteMessage,
                val.eventCategory,
                this.coverImage || localStorage.getItem('coverImage'),
                startEpoch,
                endEpoch,
                val.RSVP,
                val.eventLocation,
                null,
                val.isScheduled
            )).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success') {
                        this.toastService.showToast('Changes saved');

                        // Update eventData object
                        if (this.eventData) {
                            this.eventData.name = val.eventName;
                            this.eventData.host = val.eventHost;
                            this.eventData.invite_note = val.inviteMessage;
                            this.eventData.category = val.eventCategory;
                            this.eventData.Start_time = startEpoch;
                            this.eventData.End_time = endEpoch;
                            this.eventData.RSVP = val.RSVP;
                            this.eventData.location = val.eventLocation;
                            this.eventData.isScheduled = val.isScheduled;
                        }

                        // Encrypt and save to localStorage
                        try {
                            const encrypted = this.encryptionService.encryptData(JSON.stringify(this.eventData || {}));
                            localStorage.setItem('eventData', encrypted);
                            localStorage.setItem('formData', encrypted);
                        } catch (e) {
                            localStorage.setItem('eventData', JSON.stringify(this.eventData || {}));
                            localStorage.setItem('formData', JSON.stringify(this.eventData || {}));
                        }

                        // Refetch invite-info from server to get fresh data
                        this.eventService.getTotalMediaCount(this.eventId).subscribe({
                            next: (inviteRes: any) => {
                                if (inviteRes) {
                                    try {
                                        const encryptedInvite = this.encryptionService.encryptData(JSON.stringify(inviteRes));
                                        localStorage.setItem('invite-info', encryptedInvite);
                                        // Also update inviteData so event-detail reads fresh values
                                        localStorage.setItem('inviteData', encryptedInvite);
                                        this.inviteInfo = inviteRes;
                                    } catch (e) {
                                        localStorage.setItem('invite-info', JSON.stringify(inviteRes));
                                        // Also update inviteData so event-detail reads fresh values
                                        localStorage.setItem('inviteData', JSON.stringify(inviteRes));
                                        this.inviteInfo = inviteRes;
                                    }
                                }
                            },
                            error: (err: any) => console.error('Error refetching invite-info:', err)
                        });

                        this.originalFormValues = this.newEventForm.value;
                        this.newEventForm.patchValue(this.newEventForm.value);
                        this.newEventForm.disable();
                        this.isEditable = false;
                    } else {
                        this.toastService.showToast('Failed to update event');
                    }
                    this.isSaving = false;
                },
                error: (err: any) => {
                    // console.error('Error updating event:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Error updating event');
                    this.isSaving = false;
                }
            });
        } catch (err) {
            // console.error('Error calling updateScheduledEvent:', err); //__CONSOLE_LOG__
            this.isSaving = false;
        }
    }

    // Guest Management
    filterGuests(filter: string): void {
        this.selectedFilter = filter;
        if (filter === 'all') {
            this.collaborators = [...this.allCollaborators];
        } else {
            this.collaborators = this.allCollaborators.filter(c =>
                (c.digitalRSVP || 'pending').toLowerCase() === filter.toLowerCase()
            );
        }
    }

    get allCount(): number {
        return this.allCollaborators.length;
    }

    get yesCount(): number {
        return this.allCollaborators.filter(c => (c.digitalRSVP || '').toLowerCase() === 'yes').length;
    }

    get noCount(): number {
        return this.allCollaborators.filter(c => (c.digitalRSVP || '').toLowerCase() === 'no').length;
    }

    get maybeCount(): number {
        return this.allCollaborators.filter(c => (c.digitalRSVP || '').toLowerCase() === 'maybe').length;
    }

    async inviteUsers(): Promise<void> {
        if (!this.eventId) {
            this.toastService.showToast('We lost track of which album this is. Go back and open it again.');
            return;
        }

        try {
            (await this.eventService.getEventQR(this.eventId)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success' && res.data) {
                        this.qrImage = res.data.qr_code;
                        localStorage.setItem('qrImage', this.qrImage!);

                        if (this.isMobile) {
                            this.router.navigate(['/event-invite']);
                        } else {
                            this.openInviteDialog();
                        }
                    }
                },
                error: (err: any) => {
                    // console.error('Error fetching QR code:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to load QR code');
                }
            });
        } catch (e) {
            // console.error('Error calling getEventQR:', e); //__CONSOLE_LOG__
        }
    }

    openInviteDialog(): void {
        if (environment.features.newInviteDesign) {
            openInviteShareDialog(this.dialog, {
                eventId: this.eventId,
                eventName: this.newEventForm.get('eventName')?.value || this.eventData?.name || '',
                surface: 'event_settings_v2',
                mode: 're-invite',
                coverImageUrl: this.coverImage || '',
                hostName: this.eventData?.hostName || '',
                eventStartTime: this.newEventForm.get('eventStartTime')?.value || this.eventData?.Start_time,
            });
            return;
        }

        this.dialog.open(InviteDialogComponent, {
            width: this.isMobile ? '95vw' : '440px',
            maxWidth: '95vw',
            maxHeight: '92vh',
            panelClass: 'invite-dialog'
        });
    }

    toggleMenu(index: number): void {
        this.activeMenuIndex = this.activeMenuIndex === index ? null : index;
    }

    closeMenu(): void {
        this.activeMenuIndex = null;
    }

    async removeUser(user: any): Promise<void> {
        if (!user || !user.user_id) {
            return;
        }

        const confirmed = confirm(`Remove ${user.name || 'this user'} from the event?`);
        if (!confirmed) {
            return;
        }

        try {
            (await this.eventService.removeUser(this.eventId!, user.user_id)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success') {
                        this.allCollaborators = this.allCollaborators.filter(c => c.user_id !== user.user_id);
                        this.filterGuests(this.selectedFilter);
                        this.toastService.showToast('User removed successfully');
                        this.closeMenu();
                    }
                },
                error: (err: any) => {
                    // console.error('Error removing user:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Failed to remove user');
                }
            });
        } catch (e) {
            // console.error('Error calling removeUser:', e); //__CONSOLE_LOG__
        }
    }

    // Section Navigation (Desktop)
    changeSection(section: number): void {
        this.currentSection = section;
    }

    getUserIcon(user: any): string {
        if (user.profile_pic) {
            return user.profile_pic;
        }
        return 'assets/default-avatar.png';
    }

    getRsvpClass(rsvp: string): string {
        const r = (rsvp || 'pending').toLowerCase();
        if (r === 'yes') return 'status yes';
        if (r === 'no') return 'status no';
        if (r === 'maybe') return 'status maybe';
        return 'status pending';
    }

    getRsvpColor(rsvp: string): string {
        const r = (rsvp || 'pending').toLowerCase();
        if (r === 'yes') return '#C4F7A5';
        if (r === 'no') return '#FFD2D2';
        if (r === 'maybe') return '#FFEAD2';
        return '#E0E0E0';
    }

    confirmDelete(): void {
        const confirmed = confirm('Are you sure you want to delete this event? This action cannot be undone.');
        if (confirmed) {
            this.deleteEvent();
        }
    }

    async deleteEvent(): Promise<void> {
        if (!this.eventId) {
            this.toastService.showToast('We lost track of which album this is. Go back and open it again.');
            this.goBacktoHome();
            return;
        }

        try {
            (await this.eventService.deleteEvent(this.eventId)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success') {
                        this.toastService.showToast('Event deleted successfully!');
                        this.goBacktoHome();
                    } else {
                        this.toastService.showToast('Failed to delete event');
                    }
                },
                error: (err: any) => {
                    // console.error('Error deleting event:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Error deleting event');
                }
            });
        } catch (err) {
            // console.error('Error calling deleteEvent:', err); //__CONSOLE_LOG__
        }
    }

    confirmExit(): void {
        const confirmed = confirm('Are you sure you want to exit this event?');
        if (confirmed) {
            this.exitEvent();
        }
    }

    async exitEvent(): Promise<void> {
        if (!this.eventId) {
            this.toastService.showToast('We lost track of which album this is. Go back and open it again.');
            return;
        }

        try {
            (await this.eventService.exitEvent(this.eventId)).subscribe({
                next: (res: any) => {
                    if (res && res.result === 'success') {
                        this.toastService.showToast('You have exited the event');
                        this.goBacktoHome();
                    } else {
                        this.toastService.showToast('Failed to exit event');
                    }
                },
                error: (err: any) => {
                    // console.error('Error exiting event:', err); //__CONSOLE_LOG__
                    this.toastService.showToast('Error exiting event');
                }
            });
        } catch (err) {
            // console.error('Error calling exitEvent:', err); //__CONSOLE_LOG__
        }
    }

    goBack(): void {
        const access = localStorage.getItem('access');
        if (access === 'restricted') {
            this.router.navigate(['/open-gallery']);
        } else {
            this.router.navigate(['/home']);
        }
    }

    goBacktoHome(): void {
        const keys = [
            'eventId', 'showTimerCard', 'showInviteCard', 'access', 'isEventId',
            'event_hash', 'event_name', 'e', 'isHost', 'inviteData', 'eventData',
            'isScheduled', 'qrcode', 'savedHashtags', 'name', 'invite_resp',
            'event_subfolders', 'web_qrImage', 'web_viewer_qrImage', 'coverImage',
            'openPopup', 'formData', 'isFutureEvent'
        ];
        keys.forEach(key => localStorage.removeItem(key));
        this.router.navigate(['/home']).then(() => reloadApp('event_settings_exit'));
    }
}
